<template>
  <div>
    <!-- 同一行布局（inline） -->
    <template v-if="layout === 'inline'">
      <el-form-item v-if="showPhone" :label="phoneLabel" :prop="phoneProp">
        <el-input
          :model-value="phone"
          @update:model-value="updatePhone"
          :placeholder="phonePlaceholder"
          prefix-icon="Phone"
          maxlength="11"
          autocomplete="tel"
          :disabled="phoneDisabled"
        ></el-input>
      </el-form-item>

      <el-form-item :label="showPhone ? smsCodeLabel : ''" :prop="smsCodeProp">
        <div class="sms-code-wrapper">
          <el-input
            :model-value="smsCode"
            @update:model-value="updateSmsCode"
            :placeholder="smsCodePlaceholder"
            prefix-icon="Message"
            class="sms-code-input"
            autocomplete="one-time-code"
          ></el-input>

          <el-button
            :loading="smsLoading"
            :disabled="countdown > 0"
            @click="handleGetSmsCode"
            class="sms-btn"
          >
            {{ countdown > 0 ? `${countdown}秒后重新获取` : "获取验证码" }}
          </el-button>
        </div>
      </el-form-item>
    </template>

    <!-- 分两行布局（block） -->
    <template v-else>
      <div v-if="showPhone" class="form-item">
        <div class="form-label">{{ phoneLabel }}:</div>
        <el-form-item :prop="phoneProp">
          <el-input
            :model-value="phone"
            @update:model-value="updatePhone"
            :placeholder="phonePlaceholder"
            prefix-icon="Phone"
            maxlength="11"
            autocomplete="tel"
            :disabled="phoneDisabled"
          ></el-input>
        </el-form-item>
      </div>

      <div class="form-item" :class="{ 'no-label': !showPhone }">
        <div v-if="showPhone" class="form-label">{{ smsCodeLabel }}:</div>
        <el-form-item :prop="smsCodeProp">
          <div class="sms-code-wrapper">
            <el-input
              :model-value="smsCode"
              @update:model-value="updateSmsCode"
              :placeholder="smsCodePlaceholder"
              prefix-icon="Message"
              class="sms-code-input"
              autocomplete="one-time-code"
            ></el-input>

            <el-button
              :loading="smsLoading"
              :disabled="countdown > 0"
              @click="handleGetSmsCode"
              class="sms-btn"
            >
              {{ countdown > 0 ? `${countdown}秒后重新获取` : "获取验证码" }}
            </el-button>
          </div>
        </el-form-item>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, toRefs } from "vue";
import { ElMessage } from "element-plus";
import request from "@/api/request";

interface Props {
  phone: string;
  smsCode: string;
  apiUrl: string; // 接口 URL，必填
  layout?: "inline" | "block"; // 布局类型：inline-同一行，block-分两行
  showPhone?: boolean; // 是否显示手机号输入框
  phoneDisabled?: boolean; // 手机号输入框是否禁用
  phoneLabel?: string;
  smsCodeLabel?: string;
  phonePlaceholder?: string;
  smsCodePlaceholder?: string;
  phoneProp?: string;
  smsCodeProp?: string;
}

const props = withDefaults(defineProps<Props>(), {
  layout: "inline",
  showPhone: true,
  phoneDisabled: false,
  phoneLabel: "手机号",
  smsCodeLabel: "验证码",
  phonePlaceholder: "请输入您的手机号",
  smsCodePlaceholder: "请输入验证码",
  phoneProp: "phone",
  smsCodeProp: "smsCode",
});

const { phone, apiUrl } = toRefs(props);

const emit = defineEmits<{
  "update:phone": [value: string];
  "update:smsCode": [value: string];
}>();

// 倒计时
const countdown = ref(0);
const smsLoading = ref(false);
let countdownTimer: ReturnType<typeof setInterval> | null = null;

// 手机号是否有效
const isPhoneValid = computed(() => {
  const phoneReg = /^1[3-9]\d{9}$/;
  return phoneReg.test(phone.value);
});

// 更新手机号
const updatePhone = (value: string) => {
  emit("update:phone", value);
};

// 更新验证码
const updateSmsCode = (value: string) => {
  emit("update:smsCode", value);
};

// 开始倒计时
const startCountdown = () => {
  countdown.value = 60;
  if (countdownTimer) {
    clearInterval(countdownTimer);
  }
  countdownTimer = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      clearInterval(countdownTimer!);
      countdownTimer = null;
    }
  }, 1000);
};

// 获取短信验证码
const handleGetSmsCode = async () => {
  // 先校验手机号
  if (!isPhoneValid.value) {
    ElMessage.warning("请输入正确的手机号后获取验证码");
    return;
  }

  // 开始 loading
  smsLoading.value = true;

  try {
    const res = await request.post(apiUrl.value, { phone: phone.value });
    if (res?.code === "success") {
      ElMessage.success("验证码已发送");
      // 接口成功后开始倒计时
      startCountdown();
    } else {
      ElMessage.error(res?.message || "获取验证码失败");
    }
  } catch (error) {
    ElMessage.error("获取验证码失败，请稍后重试");
  } finally {
    // 无论成功失败都关闭 loading
    smsLoading.value = false;
  }
};

// 组件卸载时清除定时器
onBeforeUnmount(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
});
</script>

<style lang="scss" scoped>
.form-item {
  margin-bottom: 20px;

  .form-label {
    color: #333;
    font-size: 14px;
    margin-bottom: 8px;
    font-weight: 500;
  }

  &.no-label {
    margin-bottom: 0;
  }
}

.sms-code-wrapper {
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;

  .sms-code-input {
    flex: 1;
  }

  .sms-btn {
    white-space: nowrap;
    width: 120px;
    flex-shrink: 0;
  }
}
</style>
